import { FC, PropsWithChildren, ReactElement, useState } from 'react';
import {
  ColumnDef,
  ExpandedState,
  Row,
  TableOptions,
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable
} from '@tanstack/react-table';
import { Loader } from '../Loader';
import { PageControls } from '../pagination/Pagination';
import { RowView } from './Row';
import strings from './table.translations';
import { TableHeader } from './TableHeader';

export interface SubComponentProps<T> {
  row: T;
}

export interface TableProperties<T extends Record<string, unknown>> extends Omit<TableOptions<T>, 'getCoreRowModel'> {
  data: T[];
  columns: ColumnDef<T, any>[];
  loading?: boolean;
  onClick?: (row: Row<T>) => void;
  pageSize?: number;
  DetailsView?: FC<SubComponentProps<T>>;
  filterValue?: any;
  includeRecord?: (record: T, filterValue: any) => boolean;
  autoExpandIdx?: number;
  rowIdCallback?: (row: Row<T>) => string;
}

export function TableContents<T extends Record<string, unknown>>(
  props: PropsWithChildren<TableProperties<T>>
): ReactElement {
  const {
    data,
    columns,
    loading,
    onClick,
    DetailsView,
    includeRecord,
    filterValue,
    pageSize,
    autoExpandIdx,
    rowIdCallback,
    ...restTableOptions
  } = props;

  const globalFilterFn = (row: Row<T>, columnId: string, filterVal: any) => {
    let trimmedVal = typeof filterVal === 'string' ? filterVal.trim() : filterVal;
    if (!includeRecord) return true;
    return includeRecord(row.original, trimmedVal);
  };

  const [expanded, setExpanded] = useState<ExpandedState>({});
  const table = useReactTable({
    data,
    columns,
    state: {
      globalFilter: filterValue,
      expanded
    },
    initialState: {
      expanded: autoExpandIdx !== undefined && autoExpandIdx !== -1 ? { [autoExpandIdx]: true } : {},
      pagination: pageSize ? { pageSize, pageIndex: 0 } : undefined,
      sorting: [
        {
          id: 'nameAndId',
          desc: false
        }
      ]
    },
    globalFilterFn: includeRecord ? globalFilterFn : undefined,

    // Core Models
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),

    getPaginationRowModel: pageSize ? getPaginationRowModel() : undefined,
    
    //Enable Row Expansion
    getRowCanExpand: () => true,
    onExpandedChange: setExpanded,

    ...restTableOptions
  });

  const contents = table.getRowModel().rows;

  const showHeader = columns.some(column => column.header !== undefined);

  return (
    <>
      <div className={'w-full overflow-hidden'}>
        <table className="min-w-full divide-y divide-primary-std border-b">
          {showHeader && <TableHeader headerGroups={table.getHeaderGroups()} />}
          {contents && contents.length > 0 ? (
            <tbody className="divide-y divide-primary-std">
              {contents.map((row, index) => {
                return (
                  <RowView
                    key={row.id}
                    id={rowIdCallback ? rowIdCallback(row) : undefined}
                    row={row}
                    DetailsView={DetailsView}
                    onClick={onClick}
                    index={index}
                  />
                );
              })}
            </tbody>
          ) : null}
        </table>
      </div>
      {pageSize && contents && contents.length > 0 && (
        <PageControls
          canGoBackward={table.getCanPreviousPage()}
          canGoForward={table.getCanNextPage()}
          currentPage={table.getState().pagination.pageIndex}
          numberOfPages={table.getPageCount()}
          forward={table.nextPage}
          backward={table.previousPage}
        />
      )}
      {contents && contents.length === 0 && !loading ? (
        <div className="py-5 px-6 w-full flex justify-center border-t border-primary-std">
          <p className="text-xs font-medium text-primary-std uppercase py-1">{strings.nothingToShow}</p>
        </div>
      ) : loading ? (
        <div className="py-5 px-6 w-full flex justify-center bg-secondary-std border-t border-primary-std">
          <Loader />
        </div>
      ) : null}
    </>
  );
}

export default TableContents;
